import { supabase } from "@/lib/supabase";
import { Tables } from "@/types/supabase-types";
import { Item, ItemSelectResult, itemSelect, processItem } from "../../job-tracking/queries/tracking-data/items";
import {
  calculateBaseQuantity,
  itemUOMSelect,
  ItemUOMSelectResult,
} from "../../job-tracking/queries/tracking-data/item-uoms";
import { CADCurrencyConverter } from "../../job-tracking/queries/tracking-data/currency";
import { CostTaxBreakdown } from "@/features/job-tracking/queries/tracking-data/cost-tax-breakdown";
import { PurchaseTaxCode } from "@/features/job-tracking/queries/tracking-data/vendor";
import { dineroCADFromFloatingPoint } from "@/utils/dinero-util";

export interface Estimate extends Omit<EstimateSelectResult, "lines"> {
  lines: EstimateLineItem[];
}

export interface EstimateLineItem extends Omit<EstimateLineItemSelectResult, "item"> {
  /** Quantity for the line item converted to the base UOM */
  baseQuantity: number;
  item: Item;
  quotedTotal: CostTaxBreakdown;
}

interface EstimateSelectResult extends Pick<
  Tables<"Estimate">,
  "id" | "number" | "date" | "customerMessage" | "comment"
> {
  customer: Pick<Tables<"Customer">, "id" | "name">;
  lines: EstimateLineItemSelectResult[];
  customFieldValues: Tables<"EstimateCustomFieldValue">[];
}

interface EstimateLineItemSelectResult extends Pick<
  Tables<"EstimateLineItem">,
  "id" | "lineNumber" | "description" | "amount"
> {
  item: ItemSelectResult;
  job: Pick<Tables<"Job">, "id" | "name"> | null;
  /** Quantity for the line item in the selected ItemUOM */
  uomQuantity: number;
  itemUOM: ItemUOMSelectResult | null;
  taxCode: PurchaseTaxCode | null;
}

const estimateSelect = `
  id,
  number,
  date,
  customerMessage,
  comment,
  customer:Customer!inner(id,name),
  lines:EstimateLineItem!inner(
    id,
    lineNumber,
    item:Item!inner(${itemSelect}),
    description,
    uomQuantity:quantity,
    itemUOM:ItemUOM(${itemUOMSelect}),
    amount,
    taxCode:TaxCode!taxCodeId(id,name,purchaseTaxRate),
    job:Job(id, name)
  ),
  customFieldValues:EstimateCustomFieldValue(*)
`;

export async function getEstimate(
  estimateNumber: string,
  CADCurrencyConverter: CADCurrencyConverter,
): Promise<Estimate> {
  const estimate = (
    await supabase
      .from("Estimate")
      .select<typeof estimateSelect, EstimateSelectResult>(estimateSelect)
      .eq("number", estimateNumber)
      .eq("deleted", false)
      .eq("lines.deleted", false)
      .throwOnError()
      .limit(1)
      .maybeSingle()
  ).data;
  if (!estimate) throw new Error(`Failed to find an estimate with number ${estimateNumber}`);

  return processEstimate(estimate, CADCurrencyConverter);
}

function processEstimate(
  { lines: rawLines, ...estimate }: EstimateSelectResult,
  CADCurrencyConverter: CADCurrencyConverter,
): Estimate {
  const lines = rawLines
    .map((x) => processEstimateLineItem(x, CADCurrencyConverter))
    .sort((a, b) => a.lineNumber - b.lineNumber);
  return { ...estimate, lines };
}

function processEstimateLineItem(
  line: EstimateLineItemSelectResult,
  CADCurrencyConverter: CADCurrencyConverter,
): EstimateLineItem {
  return {
    ...line,
    baseQuantity: calculateBaseQuantity(line.uomQuantity, line.itemUOM),
    item: processItem(line.item, CADCurrencyConverter),
    quotedTotal: new CostTaxBreakdown({
      subtotal: dineroCADFromFloatingPoint(line.amount),
      taxCode: line.taxCode,
    }),
  };
}
